import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import { styles } from "@components/commerce/product-listing/filters/toolbar/item/color/ColorFilterItemStyles";
import type { CommonToolbarFilterProps } from "@components/commerce/product-listing/filters/toolbar/item/common/CommonToolbarFilter";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ui/components/content/text/Text";
import { Box } from "@ui/components/layout/box/Box";
import { noHoverOnTouchDevice } from "@ui/css/common";
import { Flex } from "@ui/components/layout/flex/Flex";
import { SelectDropdownItem } from "@ui/components/overlay/select-dropdown/SelectDropdown";
import { focusOutline } from "@ui/css/02.focus/focus.styles";
import { useMemo } from "react";
import { colors } from "@ui/design-tokens";

export const ColorToolbarFilter = ({
    initialFilters,
    filterName,
    items,
    refine,
    isToolbar,
    isLoading,
}: CommonToolbarFilterProps) => {
    const dropdownItems = (
        <Box sx={styles.itemsWrapper}>
            {initialFilters?.map((item) => {
                const dynamicItem = items.find((it) => it.value === item.value);
                return (
                    <SelectDropdownItem
                        key={item.label}
                        sx={{
                            ...styles.itemContainer,
                            _focusVisible: {
                                ...focusOutline,
                            },
                            _hover: {
                                outline: "none",
                            },
                        }}
                        onClick={() => {
                            refine(item.label);
                        }}
                        isDisabled={!dynamicItem}
                    >
                        <ColorPickerWithHoveredState item={item} dynamicItem={dynamicItem} />
                    </SelectDropdownItem>
                );
            })}
        </Box>
    );

    return (
        <FilterBase
            isLoading={isLoading}
            dropdownItems={dropdownItems}
            filterName={filterName}
            isToolbar={isToolbar}
        />
    );
};

const ColorPickerWithHoveredState = ({ item, dynamicItem }) => {
    const disabled = useMemo(() => !dynamicItem, [dynamicItem]);

    return (
        <Box sx={styles.item}>
            <Flex direction={"row"} alignItems={"center"} gap={1} width={"100%"}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        fontWeight: "bold",
                        width: "100%",
                        gap: 2,
                    }}
                >
                    <ColorItem
                        disabled={disabled}
                        color={item.label}
                        checked={dynamicItem?.isRefined}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                            width: "fit-content",
                            color: disabled ? "gray.200" : "black",
                            position: "relative",
                        }}
                        fontWeight={"bold"}
                    >
                        {item.label}
                        <Text color={disabled ? "gray.200" : "gray.500"} fontWeight={"bold"}>
                            ({dynamicItem?.count ?? 0})
                        </Text>
                    </Box>
                </Box>
            </Flex>
        </Box>
    );
};

export const ColorItem = ({ checked, color, disabled }) => {
    const { get: getMicrocopy } = useMicrocopy();

    const colorMicrocopy = useMemo(() => {
        return getMicrocopy(CommonCMS.shared, `uiText.color.${color.toLowerCase()}`);
    }, [color]);

    return (
        <Box p={1} py={0}>
            <Box
                sx={{
                    [noHoverOnTouchDevice]: {
                        _hover: !disabled && {
                            border: "3px solid white",
                            outline: "1px solid gray",
                        },
                    },
                    width: "18px",
                    height: "18px",
                    background: colorMicrocopy,
                    borderRadius: "100%",
                    border: checked ? "3px solid white" : "",
                    boxSizing: "border-box",
                    outline: colorMicrocopy === colors.white ? `1px solid ${colors.gray[200]}` : "",
                    ...(checked && { outline: "3px solid black" }),
                    opacity: disabled ? 0.3 : 1,
                }}
            />
        </Box>
    );
};

export default ColorToolbarFilter;
